import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { BEARER_TOKEN } from "../../../src/store/config";
import axios from "axios";
import "./topReport.css";
import { TopPatrocinioItem, TopVentasItem, TopEmperadorItem, NuevosRangosItem, NetworkPeriod } from "./types";

/* ═══════════════════════════════════════════════════════════════════
   COMPONENTE DE TABLA REUTILIZABLE
═══════════════════════════════════════════════════════════════════ */
interface TableColumn {
    key: string;
    label: string;
    align?: "left" | "center" | "right";
    width?: string;
    render?: (value: any, item: any) => React.ReactNode;
}

interface DataTableProps {
    columns: TableColumn[];
    data: any[];
    emptyMessage?: string;
    itemsPerPage?: number;
    variant?: "default" | "inactive";
}

const DataTable: React.FC<DataTableProps> = ({
    columns,
    data,
    emptyMessage = "No hay datos disponibles",
    itemsPerPage = 10,
    variant = "default"
}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const totalPages = Math.ceil(data.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedData = data.slice(startIndex, startIndex + itemsPerPage);

    const handlePrevious = () => setCurrentPage(p => Math.max(1, p - 1));
    const handleNext = () => setCurrentPage(p => Math.min(totalPages, p + 1));

    if (data.length === 0) {
        return (
            <div className="empty-state">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                </svg>
                <p>{emptyMessage}</p>
            </div>
        );
    }

    return (
        <div className="table-wrapper">
            <table className={`data-table ${variant}`}>
                <thead>
                    <tr>
                        {columns.map(col => (
                            <th key={col.key} style={{ textAlign: col.align || "left", width: col.width }}>
                                {col.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {paginatedData.map((item, idx) => (
                        <tr key={idx}>
                            {columns.map(col => (
                                <td key={col.key} style={{ textAlign: col.align || "left" }}>
                                    {col.render ? col.render(item[col.key], item) : item[col.key]}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>

            {totalPages > 1 && (
                <div className="pagination">
                    <button onClick={handlePrevious} disabled={currentPage === 1}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="15 18 9 12 15 6" />
                        </svg>
                        Anterior
                    </button>
                    <span className="page-info">
                        Página {currentPage} de {totalPages}
                    </span>
                    <button onClick={handleNext} disabled={currentPage === totalPages}>
                        Siguiente
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="9 18 15 12 9 6" />
                        </svg>
                    </button>
                </div>
            )}
        </div>
    );
};

/* ═══════════════════════════════════════════════════════════════════
   TARJETA DE REPORTE
═══════════════════════════════════════════════════════════════════ */
interface ReportCardProps {
    title: string;
    icon: React.ReactNode;
    children: React.ReactNode;
    badge?: string;
}

const ReportCard: React.FC<ReportCardProps> = ({ title, icon, children, badge }) => {
    return (
        <div className="report-card">
            <div className="card-header">
                <div className="card-title">
                    {icon}
                    <h2>{title}</h2>
                    {badge && <span className="badge">{badge}</span>}
                </div>
            </div>
            <div className="card-body">
                {children}
            </div>
        </div>
    );
};

/* ═══════════════════════════════════════════════════════════════════
   COMPONENTE PRINCIPAL
═══════════════════════════════════════════════════════════════════ */
const TopReportes: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [topPatrocinio, setTopPatrocinio] = useState<TopPatrocinioItem[]>([]);
    const [topVentas, setTopVentas] = useState<TopVentasItem[]>([]);
    const [topEmperador, setTopEmperador] = useState<TopEmperadorItem[]>([]);
    const [nuevosRangos, setNuevosRangos] = useState<NuevosRangosItem[]>([]);

    // Estados para los periodos
    const [periodOptions, setPeriodOptions] = useState<NetworkPeriod[]>([]);
    const [selectedPeriod, setSelectedPeriod] = useState<string>("");
    const valor = localStorage.getItem("USER_AUTH") ?? "valor_por_defecto";
    const user = useMemo(() => JSON.parse(valor), [valor]);
    const [isOpen, setIsOpen] = useState(false);
    const selectRef = useRef<HTMLDivElement>(null);

    // Obtener periodos disponibles
    const fetchPeriods = useCallback(async () => {
        try {
            const response = await axios.get(
                "https://api.yosoymitzen.com/v1/NetworkPeriod/GetNetworkPeriodForMLM",
                {
                    headers: {
                        Authorization: `Bearer ${BEARER_TOKEN}`,
                    },
                }
            );
            const periods = response.data.data;
            setPeriodOptions(periods);

            if (periods && periods.length > 0) {
                const networkPeriodIds = periods.map((period: NetworkPeriod) => period.networkPeriodId);
                const maxNetworkPeriodId = Math.max(...networkPeriodIds);
                setSelectedPeriod(maxNetworkPeriodId.toString());
                return maxNetworkPeriodId;
            }
            return null;
        } catch (error) {
            console.error("Error fetching periods:", error);
            return null;
        }
    }, []);

    const clearTops = () => {
        setTopPatrocinio([]);
        setTopVentas([]);
        setTopEmperador([]);
        setNuevosRangos([]);
    };

    // Obtener Top Patrocinio desde el API
    const fetchTops = useCallback(async (networkPeriodId: number) => {
        try {
            const response = await axios.post(
                "https://api.yosoymitzen.com/v1/UserNetwork/GetTopPatron",
                {
                    userId: user.userId,
                    networkPeriodId,
                },
                {
                    headers: {
                        accept: "*/*",
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${BEARER_TOKEN}`,
                    },
                }
            );

            const data = response.data?.data ?? response.data;

            if (!data) {
                clearTops();
                return;
            }
                console.log(data.topPatrons);
            setTopPatrocinio(
                Array.isArray(data.topPatrons) ? data.topPatrons : []
            );
            setTopVentas(
                Array.isArray(data.topSales) ? data.topSales : []
            );
            setTopEmperador(
                Array.isArray(data.topEmperors) ? data.topEmperors : []
            );
            setNuevosRangos(
                Array.isArray(data.topNewRanges) ? data.topNewRanges : []
            );

        } catch (error) {
            console.error("Error fetching top patron:", error);
            clearTops();
        }
    }, [user.userId]);

    // Cargar todos los datos
    const cargarDatos = useCallback(async () => {
        setLoading(true);

        // 1. Obtener periodos
        const periodId = await fetchPeriods();
        // 2. Si hay periodo seleccionado, obtener Top Patrocinio
        if (periodId) {
            await fetchTops(periodId);
        }

        setLoading(false);
    }, [fetchPeriods, fetchTops]);

    // Cargar datos al montar el componente
    useEffect(() => {
        cargarDatos();
    }, [cargarDatos]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Calcula el periodo seleccionado para mostrar
    const selectedPeriodData = periodOptions.find(
        p => p.networkPeriodId.toString() === selectedPeriod
    );

    // Manejar cambio de periodo
    const handlePeriodChange = async (periodId: string) => {
        setSelectedPeriod(periodId);
        setLoading(true);
        await fetchTops(Number(periodId));
        setLoading(false);
    };

    // Columnas para Top Patrocinio (usando los keys correctos del mapeo)
    const columnasPatrocinio: TableColumn[] = [
        { key: "top", label: "TOP", align: "center", width: "80px", render: (v) => <span className="rank-badge">#{v}</span> },
        { key: "recognitionName", label: "Nombre de Reconocimiento" },
        { key: "pp", label: "PP", align: "right", render: (v) => <span className="points">{v?.toLocaleString() ?? "0"}</span> },
        { key: "pP_Red", label: "Ptos Patrocinados (Ptos reales)", align: "right", render: (v) => <span className="points">{v?.toLocaleString() ?? "0"}</span> },
        { key: "quantityPatrons", label: "N° de Afiliados", align: "center", render: (v) => <span className="badge-small">{v ?? 0}</span> },
        { key: "phone", label: "Celular", render: (v) => <span className="phone">{v || "-"}</span> },
        { key: "department", label: "Departamento" },
        { key: "province", label: "Provincia" },
        { key: "district", label: "Distrito" },
        { key: "patron", label: "Patrocinador" },
        { key: "patronPhone", label: "Cel. Patrocinador", render: (v) => <span className="phone">{v || "-"}</span> },
        { key: "risingDiamond", label: "Diamante Ascendente" },
        { key: "risingDiamondPhone", label: "Cel. Diamante", render: (v) => <span className="phone">{v || "-"}</span> },
    ];

    const columnasVentas: TableColumn[] = [
        { key: "top", label: "TOP", align: "center", width: "80px", render: (v) => <span className="rank-badge">#{v}</span> },
        { key: "recognitionName", label: "Nombre de Reconocimiento" },
        { key: "pp", label: "PP", align: "right", render: (v) => <span className="points">{v?.toLocaleString() ?? "0"}</span> },
        { key: "userRange", label: "Rango", align: "right" },
        { key: "maximumRange", label: "Rango Máximo", align: "center" },
        { key: "phone", label: "Celular", render: (v) => <span className="phone">{v || "-"}</span> },
        { key: "department", label: "Departamento" },
        { key: "province", label: "Provincia" },
        { key: "district", label: "Distrito" },
        { key: "patron", label: "Patrocinador" },
        { key: "patronPhone", label: "Cel. Patrocinador", render: (v) => <span className="phone">{v || "-"}</span> },
        { key: "risingDiamond", label: "Diamante Ascendente" },
        { key: "risingDiamondPhone", label: "Cel. Diamante", render: (v) => <span className="phone">{v || "-"}</span> },
    ];

    const columnasEmperador: TableColumn[] = [
        { key: "top", label: "TOP", align: "center", width: "80px", render: (v) => <span className="rank-badge">#{v}</span> },
        { key: "recognitionName", label: "Nombre de Reconocimiento" },
        { key: "pp", label: "PP", align: "right", render: (v) => <span className="points">{v?.toLocaleString() ?? "0"}</span> },
        { key: "quantityVIP", label: "N° EMPRESARIOS VIP 200 (directos)", render: (v) => <span className="badge-small">{v ?? 0}</span> },
        { key: "pointsEmperor", label: "Puntos Emperador", render: (v) => <span className="badge-small">{v ?? 0}</span> },
        { key: "phone", label: "Celular", render: (v) => <span className="phone">{v || "-"}</span> },
        { key: "department", label: "Departamento" },
        { key: "province", label: "Provincia" },
        { key: "district", label: "Distrito" },
        { key: "patron", label: "Patrocinador" },
        { key: "patronPhone", label: "Cel. Patrocinador", render: (v) => <span className="phone">{v || "-"}</span> },
        { key: "risingDiamond", label: "Diamante Ascendente" },
        { key: "risingDiamondPhone", label: "Cel. Diamante", render: (v) => <span className="phone">{v || "-"}</span> },
    ];

    const columnasNuevosRango: TableColumn[] = [
        { key: "top", label: "TOP", align: "center", width: "80px", render: (v) => <span className="rank-badge">#{v}</span> },
        { key: "recognitionName", label: "Nombre de Reconocimiento" },
        { key: "pp", label: "PP", align: "right", render: (v) => <span className="points">{v?.toLocaleString() ?? "0"}</span> },
        { key: "newRange", label: "Nuevo Rango", align: "right" },
        { key: "maximumRange", label: "Rango Anterior", align: "center" },
        { key: "phone", label: "Celular", render: (v) => <span className="phone">{v || "-"}</span> },
        { key: "department", label: "Departamento" },
        { key: "province", label: "Provincia" },
        { key: "district", label: "Distrito" },
        { key: "patron", label: "Patrocinador" },
        { key: "patronPhone", label: "Cel. Patrocinador", render: (v) => <span className="phone">{v || "-"}</span> },
        { key: "risingDiamond", label: "Diamante Ascendente" },
        { key: "risingDiamondPhone", label: "Cel. Diamante", render: (v) => <span className="phone">{v || "-"}</span> },
    ];

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
                <p>Cargando reportes...</p>
            </div>
        );
    }

    return (
        <>
            <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap" rel="stylesheet" />

            <div className="reportes-container">
                {/* Header */}
                <div className="reportes-header">
                    <div className="header-title">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#0000a0" strokeWidth="1.5">
                            <path d="M21 12a9 9 0 1 0-9 9m9-9h-5m5 0-3-3m3 3-3 3" />
                            <circle cx="12" cy="12" r="3" />
                        </svg>
                        <h1>Top Reportes</h1>
                    </div>
                    <p className="header-subtitle">
                        Visualiza los mejores empresarios de tu red
                    </p>
                </div>

                {/* Periodo Bar con selector */}
                {/* Periodo Bar con selector mejorado */}
                <div className="periodo-bar">
                    <div className="periodo-selector-modern">
                        <div className="periodo-label-modern">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                <line x1="8" y1="2" x2="8" y2="6" />
                                <line x1="16" y1="2" x2="16" y2="6" />
                                <line x1="3" y1="10" x2="21" y2="10" />
                            </svg>
                            <span>Periodo</span>
                        </div>

                        {/* Custom Select Mejorado */}
                        <div className="custom-select" ref={selectRef}>
                            <div
                                className={`custom-select-trigger ${isOpen ? 'open' : ''}`}
                                onClick={() => setIsOpen(!isOpen)}
                            >
                                <div className="trigger-content">
                                    <div className="period-icon">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <circle cx="12" cy="12" r="10" />
                                            <polyline points="12 6 12 12 16 14" />
                                        </svg>
                                    </div>
                                    <div className="period-info">
                                        <span className="period-label-small">Periodo activo</span>
                                        <span className="period-value">
                                            {selectedPeriodData?.periodName && selectedPeriodData.periodName.length > 30
                                                ? `${selectedPeriodData.periodName.substring(0, 30)}...`
                                                : selectedPeriodData?.periodName || `Periodo ${selectedPeriod}`}
                                        </span>
                                    </div>
                                </div>
                                <div className={`chevron ${isOpen ? 'open' : ''}`}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="6 9 12 15 18 9" />
                                    </svg>
                                </div>
                            </div>

                            {isOpen && (
                                <div className="custom-select-dropdown">
                                    <div className="dropdown-header">
                                        <span>Seleccionar periodo</span>
                                        <span className="dropdown-count">{periodOptions.length} periodos</span>
                                    </div>
                                    <div className="dropdown-list">
                                        {periodOptions.map((period) => (
                                            <div
                                                key={period.networkPeriodId}
                                                className={`custom-select-option ${selectedPeriod === period.networkPeriodId.toString() ? 'selected' : ''}`}
                                                onClick={() => {
                                                    handlePeriodChange(period.networkPeriodId.toString());
                                                    setIsOpen(false);
                                                }}
                                            >
                                                <div className="option-dot" />
                                                <div className="option-content">
                                                    <span className="option-title">
                                                        {period.periodName || `Periodo ${period.networkPeriodId}`}
                                                    </span>
                                                    <span className="option-subtitle">
                                                        ID: {period.networkPeriodId}
                                                    </span>
                                                </div>
                                                {selectedPeriod === period.networkPeriodId.toString() && (
                                                    <div className="option-badge">
                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                                            <polyline points="20 6 9 17 4 12" />
                                                        </svg>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Grid de Reportes */}
                <div className="reports-grid">
                    {/* Top Patrocinio */}
                    <ReportCard
                        title="Top Patrocinio"
                        badge="Ranking por patrocinio"
                        icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" /><circle cx="12" cy="12" r="3" /></svg>}
                    >
                        <DataTable columns={columnasPatrocinio} data={topPatrocinio} itemsPerPage={5} />
                    </ReportCard>

                    {/* Top Ventas */}
                    <ReportCard
                        title="Top Ventas"
                        badge="Mayor puntaje real en compras"
                        icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 21V9" /><circle cx="6" cy="6" r="1" /><circle cx="18" cy="6" r="1" /></svg>}
                    >
                        <DataTable columns={columnasVentas} data={topVentas} itemsPerPage={5} />
                    </ReportCard>

                    {/* Top Emperador */}
                    <ReportCard
                        title="Top Emperador"
                        badge="Ranking por VIP 200"
                        icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M12 2 L15 9 H22 L16 14 L19 21 L12 17 L5 21 L8 14 L2 9 H9 Z" /></svg>}
                    >
                        <DataTable columns={columnasEmperador} data={topEmperador} itemsPerPage={5} />
                    </ReportCard>

                    {/* Empresarios Nuevos Rango */}
                    <ReportCard
                        title="Nuevos Rangos"
                        badge="Nuevo Rango alcanzado"
                        icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>}
                    >
                        <DataTable columns={columnasNuevosRango} data={nuevosRangos} itemsPerPage={5} />
                    </ReportCard>
                </div>
            </div>
        </>
    );
};

export default TopReportes;